import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import clsx from "clsx";
import { useResizeDetector } from "react-resize-detector";
import { useFormikContext } from "formik";

import {
  HvGlobalActions,
  HvGrid,
  HvTypography,
  HvRadioGroup,
  HvRadio,
  HvLoading,
} from "@hitachivantara/uikit-react-core";
import { HvColorPicker } from "@hitachivantara/uikit-react-lab";

import { useTheme } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { IncidentType } from "lib/api";

import FontAwesomeDropdown from "components/incidentType/Icons/IconFontAwesomeDropdown";
import GenericMap from "components/common/GenericMap";

import useStyles from "./styles";
import IconUrlInput from "../Icons/IconUrlInput";
import IconFileUpload from "../Icons/IconFileUpload";

const mapOptions = {
  zoomControl: false,
  doubleClickZoom: false,
  closePopupOnClick: false,
  dragging: false,
  trackResize: false,
  touchZoom: false,
  scrollWheelZoom: false,
};

const IncidentTypeForm: React.FC = () => {
  const { t } = useTranslation("addEditIncidentType");
  const classes = useStyles();
  const theme = useTheme();

  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<Partial<IncidentType>>();

  const { appearance: { color } = {} } = values;

  const [colorValue, setColorValue] = useState(
    color || theme.hv.palette.semantic.sema4
  );
  const [isFileUploading, setIsFileUploading] = useState(false);

  useEffect(() => {
    setFieldValue("appearance.color", colorValue);
    setFieldTouched("appearance.color", true);
  }, [colorValue, setFieldTouched, setFieldValue]);

  const onFileUpload = useCallback(
    (uploading: boolean) => setIsFileUploading(uploading),
    [setIsFileUploading]
  );

  const mapMarker = useMemo(() => {
    return { ...values?.appearance };
  }, [values?.appearance]);

  const { width = 0, ref: mapContainerRef } = useResizeDetector();
  const mapRef = useRef<L.Map>();

  useEffect(() => {
    if (width && mapRef.current != null) {
      mapRef.current.invalidateSize();
    }
  }, [width]);

  if (!values.appearance) return <></>;

  return (
    <HvGrid className={classes.root} container>
      <HvGrid item sm={12}>
        <HvGlobalActions
          title={t("sections.mapMarker.title")}
          variant="section"
        />
        <HvTypography variant="normalText">
          {t("sections.mapMarker.description")}
        </HvTypography>
      </HvGrid>

      <HvGrid container className={classes.fields} item xs={12}>
        <HvGrid item xs={12} md={6}>
          <HvColorPicker
            className={classes.colorPicker}
            label={t("mapMarker.color.label")}
            required
            value={colorValue}
            onChange={(c) => {
              setColorValue(c);
            }}
          />
          <HvRadioGroup
            label={t("mapMarker.iconType.label")}
            name="iconType"
            orientation="vertical"
            required
            className={classes.radioGroup}
            value={values.appearance.icon_type}
            onChange={(_evt, newValue) => {
              if (newValue) {
                setFieldValue("appearance", {
                  color: colorValue,
                  icon: null,
                  icon_type: newValue,
                  icon_attachment: null,
                });

                setFieldTouched("appearance.icon_type", true, false);
                setFieldTouched("appearance.icon", false, false);
              }
            }}
          >
            <HvRadio
              label={t("mapMarker.iconType.options.noIcon.label")}
              value="none"
            />
            <HvRadio
              id="fontAwesomeIconRadio"
              label={t("mapMarker.iconType.options.fontAwesomeIcon.label")}
              value="fontawesome"
            />
            <FontAwesomeDropdown />
            <HvRadio
              id="uploadIconRadio"
              label={t("mapMarker.iconType.options.uploadIcon.label")}
              value="attachment_id"
            />
            <IconFileUpload onFileUpload={onFileUpload} />
            <HvRadio
              id="urlIconRadio"
              label={t("mapMarker.iconType.options.urlIcon.label")}
              value="url"
            />
            <IconUrlInput />
          </HvRadioGroup>
        </HvGrid>
        <HvGrid
          item
          xs={12}
          md={6}
          className={classes.mapContainer}
          ref={mapContainerRef}
        >
          <div
            className={clsx(classes.overlay, {
              [classes.blur]: isFileUploading,
            })}
          >
            <HvLoading
              classes={{ root: classes.loading }}
              hidden={!isFileUploading}
            />
          </div>
          <GenericMap
            marker={mapMarker}
            disableSearchBox
            showMarkerAtInicialCenter
            className={classes.map}
            mapOptions={mapOptions}
            mapRef={mapRef}
          />
        </HvGrid>
      </HvGrid>
    </HvGrid>
  );
};

export default IncidentTypeForm;
